if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 768px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.dotButtons = this.elements.viewer.querySelectorAll('.slider-counter--dots .slider-counter__link');
      if (this.dotButtons.length) {
        this.setActiveDot(1);
        this.dotButtons.forEach((button, index) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const requestedIndex = Number(button.dataset.slideIndex);
            const targetIndex = Number.isFinite(requestedIndex) ? requestedIndex : index;

            if (!this.elements.viewer.slider || !this.elements.viewer.sliderItemsToShow?.length) return;
            const clampedIndex = Math.max(0, Math.min(this.elements.viewer.sliderItemsToShow.length - 1, targetIndex));
            const targetSlide = this.elements.viewer.sliderItemsToShow[clampedIndex];
            if (!targetSlide) return;

            this.elements.viewer.slider.scrollTo({
              left: targetSlide.offsetLeft,
              behavior: 'smooth'
            });
          });
        });
      }
      this.elements.viewer.addEventListener('slideChanged', this.onDotsChanged.bind(this));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
      this.enableDragScroll();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    onDotsChanged(event) {
      if (!this.dotButtons.length) return;
      this.setActiveDot(event.detail.currentPage);
    }

    setActiveDot(page) {
      if (!this.dotButtons.length) return;
      this.dotButtons.forEach((button) => {
        button.classList.remove('slider-counter__link--active');
        button.removeAttribute('aria-current');
      });
      const index = Math.max(0, Math.min(this.dotButtons.length - 1, page - 1));
      this.dotButtons[index].classList.add('slider-counter__link--active');
      this.dotButtons[index].setAttribute('aria-current', true);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    enableDragScroll() {
      if (!this.mql.matches) return;
      const slider = this.elements.viewer.querySelector('.slider');
      if (!slider) return;

      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let hasDragged = false;

      const onMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeft - walk;
        
        if (Math.abs(x - startX) > 5) {
          hasDragged = true;
        }
      };

      const onMouseUp = () => {
        isDown = false;
        slider.classList.remove('is-dragging');
        
        // Reativa scroll suave e snap gradualmente
        slider.style.scrollBehavior = 'smooth';
        
        setTimeout(() => {
          slider.style.scrollSnapType = '';
        }, 100);
        
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      slider.addEventListener('mousedown', (event) => {
        if (event.button !== 0) return;
        isDown = true;
        hasDragged = false;
        startX = event.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
        
        slider.classList.add('is-dragging');
        slider.style.scrollBehavior = 'auto';
        slider.style.scrollSnapType = 'none';

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
      });

      slider.addEventListener('click', (event) => {
        if (hasDragged) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          hasDragged = false;
        }
      }, true);
      
      // Prevent image drag ghost
      slider.querySelectorAll('img').forEach(img => {
          img.addEventListener('dragstart', e => e.preventDefault());
      });
    }
  });
}
